// src/api/apiClient.js
import axios from 'axios';
import { logout } from '../features/account/accountSlice';
import { RESET_STATE } from '../store/resetActions';
import { setAcceptanceRequired } from '../features/disclaimer/disclaimerSlice';
import { showPermissionDenied } from '../features/notifications/notificationsSlice';
import { IS_IDENTITY_BUILD, RETAIL_API_URL } from '../config/retailIdentity';
import { engineProxyUrl } from '../auth/identity/engineRoutes';
import { IdentitySignedOutError, retailSession } from '../auth/identity/retailSession';

// Prefer env override for local testing; fallback to production API v2
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.dwlf.co.uk/v2';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * Identity build only: an engine read retail-api serves goes there with the identity token, and
 * never carries the SPT token; only GETs, because retail-api serves reads only. Returns false for
 * anything left for SPT. A retry of a retail-api request is already rewritten, so it keeps its
 * target and takes the current identity token.
 */
const sendToRetailEngine = async (config) => {
  if (!config.retailEngine) {
    const isRead = (config.method ?? 'get').toLowerCase() === 'get';
    const proxied = IS_IDENTITY_BUILD && isRead ? engineProxyUrl(config.url) : null;
    if (proxied === null) {
      return false;
    }
    config.baseURL = RETAIL_API_URL;
    config.url = proxied;
    config.retailEngine = true;
  }
  const token = await retailSession.accessToken();
  if (token === null) {
    throw new IdentitySignedOutError();
  }
  config.headers.Authorization = `Bearer ${token}`;
  return true;
};

/** A retail-api 401: refresh the identity session once and retry; it never touches the SPT session. */
const retryRetailEngine = async (error) => {
  const original = error.config;
  original._retried = true;
  if ((await retailSession.refresh()) === null) {
    throw new IdentitySignedOutError();
  }
  return apiClient(original);
};

export const setupInterceptors = (store) => {
  // Add token to requests if logged in
  apiClient.interceptors.request.use(async (config) => {
    if (await sendToRetailEngine(config)) {
      return config;
    }
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Track whether a refresh is already in flight to avoid infinite loops
  let isRefreshing = false;
  let refreshSubscribers = [];
  const onRefreshed = (newToken) => refreshSubscribers.forEach(({ resolve }) => resolve(newToken));
  const onRefreshFailed = (error) => refreshSubscribers.forEach(({ reject }) => reject(error));
  const addRefreshSubscriber = (resolve, reject) => refreshSubscribers.push({ resolve, reject });

  // Add a response interceptor to handle token expiration and disclaimer enforcement
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (originalRequest?.retailEngine && error.response?.status === 401) {
        // Never the SPT refresh or logout below: this request did not carry the SPT session.
        return originalRequest._retried ? Promise.reject(error) : retryRetailEngine(error);
      }

      if (error.response?.status === 401 && !originalRequest._retried) {
        const storedRefreshToken = localStorage.getItem('refreshToken');

        if (storedRefreshToken) {
          // If a refresh is already in flight, queue this request
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              addRefreshSubscriber(
                (newToken) => {
                  originalRequest._retried = true; // Prevent refresh cascade
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  resolve(apiClient(originalRequest));
                },
                reject
              );
            });
          }

          originalRequest._retried = true;
          isRefreshing = true;

          try {
            const { data } = await axios.post(`${API_BASE_URL}/accounts/refresh`, {
              refreshToken: storedRefreshToken,
            });

            const newAccessToken = data.accessToken || data.token;
            localStorage.setItem('authToken', newAccessToken);
            if (data.refreshToken) {
              localStorage.setItem('refreshToken', data.refreshToken);
            }

            apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
            onRefreshed(newAccessToken);
            refreshSubscribers = [];
            isRefreshing = false;

            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            // Refresh failed — reject queued requests and force logout
            onRefreshFailed(refreshError);
            refreshSubscribers = [];
            isRefreshing = false;
            localStorage.removeItem('refreshToken');
            delete apiClient.defaults.headers.common.Authorization; // Clear stale header
            store.dispatch(logout({ reason: 'expired' }));
            store.dispatch({ type: RESET_STATE, payload: { logoutReason: 'expired' } });
            return Promise.reject(refreshError); // Don't hang the original request
          }
        } else {
          // No refresh token — only log out if this wasn't an unauthenticated auth endpoint
          // (e.g. a failed login attempt returns 401 but shouldn't trigger "session expired")
          // Use an explicit allowlist so authenticated /accounts/* endpoints still trigger logout
          const UNAUTHENTICATED_ENDPOINTS = ['/accounts/login', '/accounts/register', '/accounts/refresh'];
          // Extract pathname from URL for precise matching (avoid false positives like /accounts/login/callback)
          const getPathname = (url) => {
            try {
              // Handle both full URLs and relative paths
              return new URL(url, API_BASE_URL).pathname;
            } catch {
              // If URL parsing fails, treat as relative path
              return url?.split('?')[0]?.split('#')[0] || url;
            }
          };
          const pathname = getPathname(originalRequest.url);
          const isUnauthenticatedEndpoint = UNAUTHENTICATED_ENDPOINTS.includes(pathname);
          if (!isUnauthenticatedEndpoint) {
            delete apiClient.defaults.headers.common.Authorization; // Clear stale header
            store.dispatch(logout({ reason: 'expired' }));
            store.dispatch({ type: RESET_STATE, payload: { logoutReason: 'expired' } });
          }
        }
      }

      // Handle 403 errors
      if (error.response?.status === 403) {
        if (error.response?.data?.error === 'DISCLAIMER_NOT_ACCEPTED') {
          // Show disclaimer modal for disclaimer-related 403
          store.dispatch(setAcceptanceRequired({
            requiredVersion: error.response.data.requiredVersion,
            acceptedVersion: error.response.data.acceptedVersion,
            reason: error.response.data.reason,
          }));
        } else if (!isLegacySignUpClosed(error)) {
          // Show permission denied notification for RBAC 403
          const requiredPermission = error.response?.data?.requiredPermission;
          const message = error.response?.data?.message;
          store.dispatch(showPermissionDenied({
            requiredPermission,
            message,
          }));
        }
      }

      return Promise.reject(error);
    }
  );
};

/**
 * A legacy-build google-login 403: sign-up is closed, and the login page answers it with the
 * waitlist (DWLF-584), so it is not shown as a permission the account lacks.
 */
function isLegacySignUpClosed(error) {
  return !IS_IDENTITY_BUILD && Boolean(error.config?.url?.endsWith('/accounts/google-login'));
}

export default apiClient;
